// O ajuste de desembolso a partir do RECORTE por meses (planning-workspace): o item listado
// no recorte já abre com os meses selecionados como origem e com o valor máximo que ele tem
// disponível NAQUELES meses, sem navegar para outra tela.
//
// O formulário é o mesmo para as duas políticas; quem o usa diz como gravar
// (`submitLines`): sob Ampli cria um `disbursement_adjustments` por mês de origem usado, na
// tela legada um `advancements`. O valor digitado é repartido pelos meses de origem do mais
// antigo ao mais novo (`splitAcrossMonths`), sem passar do que cada mês tem.
import { useState } from "react";

import { parseCentsFromReais } from "./AmpliMonthDisbursementSection";
import { competenceText, money } from "./format";
import { splitAcrossMonths } from "./slice";

const COMPETENCE = /^\d{4}-(0[1-9]|1[0-2])$/;

const reaisInput = (cents) =>
  (cents / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });

const SliceAdjustForm = ({ label, availability, submitLines, onCancel, onDone }) => {
  const maxCents = availability.reduce((sum, month) => sum + month.availableCents, 0);
  const [amount, setAmount] = useState(() => reaisInput(maxCents));
  const [toCompetence, setToCompetence] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const amountCents = parseCentsFromReais(amount);
  const originCompetences = availability.map((month) => month.competence);
  const destinationIsOrigin = originCompetences.includes(toCompetence);
  const lines = Number.isSafeInteger(amountCents) ? splitAcrossMonths(amountCents, availability) : null;
  const destinationValid = COMPETENCE.test(toCompetence) && !destinationIsOrigin;
  const canConfirm = lines !== null && destinationValid && !saving;
  const maxText = money(maxCents);

  const confirm = async () => {
    if (!canConfirm) return;
    setSaving(true);
    setError(null);
    try {
      const result = await submitLines(lines, toCompetence, description);
      if (!result?.ok) {
        setError(result?.message ?? "Não foi possível mover o valor. Tente novamente.");
        return;
      }
      await onDone?.(result);
    } catch {
      setError("Não foi possível mover o valor. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="ampli-slice-adjust"
      aria-label={`Ajustar desembolso de ${label}`}
      onSubmit={(event) => {
        event.preventDefault();
        confirm();
      }}
    >
      <p>
        <strong>{`Origem: ${originCompetences.map(competenceText).join(", ")}`}</strong>
      </p>
      <p>{`Máximo disponível nesses meses: ${maxText}`}</p>
      <label>
        <span>Valor a mover</span>
        <input
          type="text"
          aria-label="Valor a mover"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          placeholder="0,00"
        />
      </label>
      {Number.isSafeInteger(amountCents) && lines === null ? (
        <p className="error-message" role="alert">
          {`O valor passa do máximo disponível nos meses selecionados (${maxText}).`}
        </p>
      ) : null}
      <label>
        <span>Mês de destino</span>
        <input
          type="month"
          aria-label="Mês de destino"
          value={toCompetence}
          onChange={(event) => setToCompetence(event.target.value)}
        />
      </label>
      {destinationIsOrigin ? (
        <p className="error-message" role="alert">
          O mês de destino não pode estar entre os meses de origem.
        </p>
      ) : null}
      <label>
        <span>Descrição do ajuste (opcional)</span>
        <input
          type="text"
          aria-label="Descrição do ajuste"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>
      {error ? (
        <p className="error-message" role="alert">
          {error}
        </p>
      ) : null}
      <div className="ampli-form-actions">
        <button type="button" className="button button-ghost small" onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" className="button button-accent small" disabled={!canConfirm}>
          {saving ? "Movendo…" : "Confirmar ajuste"}
        </button>
      </div>
    </form>
  );
};

export default SliceAdjustForm;
